import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, ArrowLeftIcon, MenuIcon, StarIcon, MapPinIcon, XIcon } from 'lucide-react';
import { restaurants, cantones } from '../data/mockData';
interface NavbarProps {
  currentView: 'home' | 'canton' | 'restaurant' | 'owner-form';
  onNavigate: (view: 'home' | 'canton' | 'restaurant' | 'owner-form') => void;
  onBack: () => void;
  onSelectRestaurant: (id: string) => void;
  onShowOwnerForm: () => void;
}
export function Navbar({
  currentView,
  onNavigate,
  onBack,
  onSelectRestaurant,
  onShowOwnerForm
}: NavbarProps) {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const normalizeText = (text: string) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const results = query.trim().length >= 2 ? restaurants.filter(r => {
    const q = normalizeText(query);
    return normalizeText(r.name).includes(q) || normalizeText(r.description).includes(q) || r.tags.some(t => normalizeText(t).includes(q)) || normalizeText(r.categoryId).includes(q) || r.menuItems.some(m => normalizeText(m.name).includes(q) || normalizeText(m.description).includes(q));
  }) : [];
  const showDropdown = isFocused && query.trim().length >= 2;
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelect = (id: string) => {
    setQuery('');
    setIsFocused(false);
    onSelectRestaurant(id);
  };
  return <nav className="sticky top-0 z-50 w-full bg-bg-primary/95 backdrop-blur-md border-b border-border" data-id="element-147">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-148">
        <div className="flex justify-between items-center h-20" data-id="element-149">
          <div className="flex items-center gap-4" data-id="element-150">
            {currentView !== 'home' && <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-bg-secondary transition-colors text-text-secondary" aria-label="Volver" data-id="element-151">
                <ArrowLeftIcon className="w-6 h-6" data-id="element-152" />
              </button>}
            <button onClick={() => onNavigate('home')} className="flex items-center" data-id="element-153">
              <img src="https://cdn.magicpatterns.com/uploads/nazWxRr8f6e5uH7jzbqQFC/Distrito_LS_logo.png" alt="Distrito LS" className="h-14 w-auto" data-id="element-154" />
            </button>
          </div>

          <div className="hidden md:flex flex-1 max-w-md mx-8" ref={wrapperRef} data-id="element-155">
            <div className="relative w-full" data-id="element-156">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-157">
                <SearchIcon className="h-5 w-5 text-text-muted" data-id="element-158" />
              </div>
              <input type="text" value={query} onChange={e => setQuery(e.target.value)} onFocus={() => setIsFocused(true)} placeholder="Buscar restaurantes, comida..." className="block w-full pl-11 pr-9 py-2.5 border border-border rounded-full leading-5 bg-bg-secondary placeholder-text-muted text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-gold/50 focus:border-brand-gold/50 transition-colors font-body sm:text-sm" data-id="element-159" />
              {query && <button onClick={() => setQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-white transition-colors" data-id="element-160">
                  <XIcon className="w-4 h-4" data-id="element-161" />
                </button>}

              {/* Navbar Search Dropdown */}
              {showDropdown && <div className="absolute top-full left-0 right-0 mt-2 bg-bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-50" data-id="element-162">
                  {results.length > 0 ? <div className="max-h-72 overflow-y-auto" data-id="element-163">
                      {results.slice(0, 5).map(r => {
                  const canton = cantones.find(c => c.id === r.cantonId);
                  return <button key={r.id} onClick={() => handleSelect(r.id)} className="w-full flex items-center gap-3 p-3 hover:bg-bg-secondary transition-colors text-left border-b border-border last:border-b-0" data-id="element-164">
                            <img src={r.image} alt={r.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" data-id="element-165" />
                            <div className="flex-1 min-w-0" data-id="element-166">
                              <p className="font-body font-bold text-sm text-white truncate" data-id="element-167">
                                {r.name}
                              </p>
                              <p className="font-body text-xs text-text-muted truncate" data-id="element-168">
                                {canton?.name} · {r.priceRange}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0" data-id="element-169">
                              <StarIcon className="w-3 h-3 text-brand-gold fill-brand-gold" data-id="element-170" />
                              <span className="font-body text-xs font-bold text-white" data-id="element-171">
                                {r.rating}
                              </span>
                            </div>
                          </button>;
                })}
                    </div> : <div className="p-4 text-center" data-id="element-172">
                      <p className="font-body text-sm text-text-muted" data-id="element-173">
                        Sin resultados para "
                        <span className="text-white" data-id="element-174">{query}</span>"
                      </p>
                    </div>}
                </div>}
            </div>
          </div>

          <div className="flex items-center gap-4" data-id="element-175">
            <div className="hidden md:block" data-id="element-176">
              <button onClick={onShowOwnerForm} className="px-4 py-2 text-sm font-body font-semibold text-brand-gold border border-brand-gold/30 rounded-lg hover:bg-brand-gold/10 transition-colors" data-id="element-177">
                ¿Dueño de restaurante?
              </button>
            </div>
            <button className="md:hidden p-2 text-text-primary" data-id="element-178">
              <MenuIcon className="w-6 h-6" data-id="element-179" />
            </button>
          </div>
        </div>
      </div>
    </nav>;
}